import { useState } from "react";
import { toast } from "sonner";
import { api, errMsg, formatRupiah } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ProofButton } from "@/components/admin/TxExtras";
import { useLiveRows, NewBadge } from "@/components/admin/LiveFeed";
import { CopyText } from "@/components/admin/CopyText";
import { ExportButton, cleanParams } from "@/components/admin/LogTools";
import {
  GAMES, PanelHeader, GameFilter, SearchBox, StatusPill, DecisionButtons, TableShell,
  usePersistedFilter, filterRows, countUnseen, bucketOf, fmtTime,
} from "@/components/admin/TxKit";

export const Th = ({ children }) => <th className="whitespace-nowrap px-4 py-3 text-left font-semibold">{children}</th>;
export const InjectBadge = ({ row }) =>
  row.source === "inject" ? (
    <span className="ml-2 rounded bg-fuchsia-500/15 px-1.5 py-0.5 text-[10px] font-bold text-fuchsia-300" title={`Inject oleh ${row.injected_by}`} data-testid={`inject-badge-${row.id}`}>
      INJECT · {row.injected_by}
      {row.note ? <span className="ml-1 font-normal text-fuchsia-200/80">· {row.note}</span> : null}
    </span>
  ) : null;

export const Td = ({ children, className = "", ...rest }) => <td className={`px-4 py-3.5 align-middle ${className}`} {...rest}>{children}</td>;
export const WaLink = ({ wa }) => (wa ? (
  <a href={`https://wa.me/${wa.replace(/^0/, "62")}`} target="_blank" rel="noopener noreferrer" className="whitespace-nowrap text-xs font-semibold text-emerald-400 hover:underline">{wa}</a>
) : <span className="text-zinc-600">—</span>);

const OrderRow = ({ o, isNew, canManage, decide }) => (
  <tr className={`border-t border-white/5 transition-colors hover:bg-white/[0.02] ${isNew ? "bg-emerald-500/[0.06]" : ""}`} data-testid={`order-row-${o.id}`}>
    <Td className="whitespace-nowrap text-xs font-semibold text-sky-300"><NewBadge show={isNew} id={o.id} />{o.product_name}</Td>
    <Td className="whitespace-nowrap"><CopyText value={o.game_id} label="ID" className="font-mono font-bold text-white" testid={`order-gameid-${o.id}`} /><InjectBadge row={o} />{o.nickname ? <div className="mt-0.5 text-[11px] font-semibold text-emerald-400" data-testid={`order-nick-${o.id}`}>✓ {o.nickname}</div> : null}</Td>
    <Td className="whitespace-nowrap font-extrabold text-[#ffd700]">{o.chips}</Td>
    <Td className="whitespace-nowrap font-bold">{formatRupiah(o.price)}</Td>
    <Td><WaLink wa={o.wa_number} /></Td>
    <Td><ProofButton proofId={o.proof_id} /></Td>
    <Td className="whitespace-nowrap text-xs text-zinc-500">{fmtTime(o.created_at)}</Td>
    <Td><StatusPill status={o.status} testid={`order-status-${o.id}`} /></Td>
    <Td>
      {canManage && bucketOf(o.status) === "pending" ? (
        <DecisionButtons id={o.id} onConfirm={() => decide(o.id, "completed")} onReject={() => decide(o.id, "cancelled")} />
      ) : <span className="font-mono text-[11px] text-zinc-500">{o.decided_by || o.injected_by || "—"}</span>}
    </Td>
  </tr>
);

export const OrdersPanel = () => {
  const { can } = useAuth();
  const canManage = can("orders", "manage");
  const { rows, newIds, load, markSeen } = useLiveRows("/admin/orders", "topup");
  const [game, setGame] = usePersistedFilter("game_filter_orders", "all");
  const [q, setQ] = useState("");
  const pickGame = (g) => {
    setGame(g);
    if (g !== "all") markSeen(g);
  };

  const decide = async (id, next) => {
    try {
      await api.patch(`/admin/orders/${id}`, { status: next });
      toast.success(next === "completed" ? "Transaksi dikonfirmasi" : "Transaksi ditolak");
      load();
    } catch (e) {
      toast.error(errMsg(e, "Gagal update"));
    }
  };

  const byGame = filterRows(rows, game, "all", "", []);
  const shown = filterRows(byGame, "all", "all", q, ["game_id", "wa_number", "id", "chips"]);
  const totalRp = shown.reduce((s, o) => s + (o.price || 0), 0);

  return (
    <section className="space-y-5" data-testid="admin-orders-panel">
      <PanelHeader title="Transaksi Top Up Masuk" subtitle={`Kelola dan proses top up ${game === "all" ? "semua game" : GAMES[game]}`} pending={byGame.length} />
      <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-white/5 bg-[#101017] p-4">
        <SearchBox value={q} onChange={setQ} placeholder="Cari Player ID, WhatsApp…" />
        <GameFilter value={game} onChange={pickGame} unseen={countUnseen(rows)} />
        {canManage ? <ExportButton url="/admin/orders/export" params={cleanParams({ product_id: game === "all" ? "" : game, q })} testid="orders-export" /> : null}
      </div>
      {!canManage ? <div className="rounded-xl border border-white/5 bg-[#13131a] px-4 py-2 text-xs text-zinc-500" data-testid="orders-readonly-note">Mode lihat saja — Anda tidak bisa mengubah status.</div> : null}
      <TableShell shown={shown.length} total={byGame.length} testid="admin-orders-table">
        <thead className="bg-[#14141c] text-[11px] uppercase tracking-widest text-zinc-500">
          <tr><Th>Game</Th><Th>Player ID</Th><Th>Paket</Th><Th>Harga</Th><Th>WhatsApp</Th><Th>Bukti</Th><Th>Waktu</Th><Th>Status</Th><Th>Ubah Status</Th></tr>
        </thead>
        <tbody>
          {shown.length === 0 ? (
            <tr><td colSpan={9} className="p-10 text-center text-zinc-500" data-testid="orders-empty">Tidak ada transaksi yang menunggu diproses.</td></tr>
          ) : shown.map((o) => <OrderRow key={o.id} o={o} isNew={newIds.has(o.id)} canManage={canManage} decide={decide} />)}
        </tbody>
        {shown.length ? (
          <tfoot className="border-t border-white/10 bg-[#14141c] text-xs">
            <tr>
              <td colSpan={3} className="px-4 py-3 font-semibold uppercase tracking-widest text-zinc-500">Total ({shown.length} transaksi)</td>
              <td colSpan={6} className="px-4 py-3" data-testid="orders-total">
                <div className="font-extrabold text-[#ffd700]">{formatRupiah(totalRp)}</div>
              </td>
            </tr>
          </tfoot>
        ) : null}
      </TableShell>
    </section>
  );
};
